import * as turf from '@turf/turf';
import { kml } from '@tmcw/togeojson';
import { Modal } from 'antd';
import { GoogleMap, Polygon as GooglePolygon, Marker } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

//APP
import { calculoAreaCoord } from '../../../../../../../../../../../../../../services/Afins';
import { errorAlert, successAlert, warningAlert } from '../../../../../../../../../../../../../../utils/alert';
import { baixarGridKML, gerarArquivoShp } from './meta';
import { calcularCentroTurf, criarPoligono } from './meta';

//COMPONENTS
import Carregando from '../../../../../../../../../../../../../../Components/CustomUi/loading';
import Configuracoes from './components/config';
import { Render } from '../../../../../../../../../../../../../../Components/CustomUi/render';
import { GridMalha, GridPolygon, MalhaGrid } from './components/gerarMalha';
import { CorrecaoSobreposicao, PreparacaoEdicao } from './components/config/abaMapa';



// Casas decimais usadas para considerar dois vértices na "mesma coordenada" (~1cm)
const PRECISAO_COORDENADA = 7

// Um polígono válido precisa de no mínimo 3 vértices distintos
const MINIMO_VERTICES_POLIGONO = 3

// Acima desta variação de área a correção é considerada arriscada e não é aplicada
const LIMITE_VARIACAO_AREA = 5

// Teto de passadas da correção — trava de segurança, o loop para sozinho ao zerar
const MAX_PASSOS_CORRECAO = 10

/**
 * Acima deste número de vértices o modo de edição fica inviável.
 *
 * O gargalo não é desenhar o contorno — um polígono não editável com 14 mil
 * vértices renderiza sem problema. É o `editable` do Google Maps, que cria um
 * marker arrastável por vértice MAIS um "midpoint" entre cada par: ~28 mil
 * overlays com listeners de mouse, recriados ao ligar a edição e reprojetados a
 * cada pan/zoom. Não há como pedir à API para instanciar só os handles visíveis.
 */
const LIMITE_VERTICES_EDICAO = 2000

/**
 * Tolerâncias tentadas, em metros, para trazer o contorno abaixo do limite.
 *
 * Começamos na mais conservadora: a maioria dos contornos gigantes vem de
 * registro por trator, onde os vértices excedentes estão em trechos retos e uma
 * tolerância de 1m já resolve. Só escalamos quando não basta.
 */
const TOLERANCIAS_SIMPLIFICACAO_AUTO = [1, 2, 3, 5, 8, 12, 20]

type Coordenada = { lat: number; lng: number }

/** Chave de comparação de coordenada — arredonda para evitar ruído de ponto flutuante */
const chaveDeCoordenada = (lat: number, lng: number) =>
    `${lat.toFixed(PRECISAO_COORDENADA)}_${lng.toFixed(PRECISAO_COORDENADA)}`

/**
 * No fluxo de KML o anel normalmente vem fechado (último ponto repete o primeiro),
 * ao contrário do desenho manual em Leaflet. Tratamos os dois casos.
 */
const anelFechado = (coords: Coordenada[]) => {
    if (coords.length < 2) return false
    const primeiro = coords[0]
    const ultimo = coords[coords.length - 1]
    return chaveDeCoordenada(primeiro.lat, primeiro.lng) === chaveDeCoordenada(ultimo.lat, ultimo.lng)
}

/** Conta vértices desconsiderando o ponto que apenas fecha o anel */
const contarVerticesReais = (coords: Coordenada[]) => {
    if (coords.length < 2) return coords.length
    return anelFechado(coords) ? coords.length - 1 : coords.length
}

/** Quantos vértices do anel estão exatamente nas mesmas coordenadas */
const contarDuplicadosDoAnel = (coords: Coordenada[]) => {
    const lista = anelFechado(coords) ? coords.slice(0, -1) : coords
    if (lista.length <= MINIMO_VERTICES_POLIGONO) return 0

    const vistos = new Set<string>()
    let duplicados = 0

    lista.forEach(({ lat, lng }) => {
        const chave = chaveDeCoordenada(lat, lng)
        if (vistos.has(chave)) duplicados += 1
        else vistos.add(chave)
    })

    return duplicados
}

/** Remove vértices que estão exatamente nas mesmas coordenadas */
const removerDuplicadosDoAnel = (coords: Coordenada[]) => {
    const fechado = anelFechado(coords)
    const lista = fechado ? coords.slice(0, -1) : coords

    const vistos = new Set<string>()
    const unicos: Coordenada[] = []

    lista.forEach(({ lat, lng }) => {
        const chave = chaveDeCoordenada(lat, lng)
        if (!vistos.has(chave)) {
            vistos.add(chave)
            unicos.push({ lat, lng })
        }
    })

    const removidos = lista.length - unicos.length
    if (unicos.length < MINIMO_VERTICES_POLIGONO) return { coords, removidos: 0 }

    return {
        coords: fechado ? [...unicos, { ...unicos[0] }] : unicos,
        removidos,
    }
}

/** Converte o anel em um polígono turf válido para medição */
const paraPoligonoTurf = (coords: Coordenada[]) => {
    const anel = coords.map(({ lat, lng }) => [lng, lat])
    if (!anelFechado(coords)) anel.push([coords[0].lng, coords[0].lat])
    return turf.polygon([anel])
}

/** Quantidade de auto-interseções (vértices sobrepostos) do contorno */
const contarKinks = (coords: Coordenada[]) => {
    try {
        if (coords.length < MINIMO_VERTICES_POLIGONO) return 0
        return turf.kinks(paraPoligonoTurf(coords)).features.length
    } catch {
        return 0
    }
}

/** Área do contorno em hectares */
const areaEmHectares = (coords: Coordenada[]) => {
    try {
        if (coords.length < MINIMO_VERTICES_POLIGONO) return 0
        return turf.area(paraPoligonoTurf(coords)) / 10000
    } catch {
        return 0
    }
}

/**
 * Corrige o contorno com o mínimo de alteração possível.
 *
 * Estratégia em cascata, do menos ao mais invasivo:
 *   1. remover vértices com coordenadas iguais — é a causa mais comum, já que o
 *      contorno é registrado por trator e uma máquina parada grava a mesma
 *      coordenada várias vezes. Não move nenhum vértice e não altera a área;
 *   2. turf.unkinkPolygon, só se ainda sobrar sobreposição — divide o anel no
 *      ponto de cruzamento e mantém a maior parte. Preserva as coordenadas
 *      exatas (não desloca vértices nem infla a contagem).
 *
 * A função é pura: serve para a prévia e para a aplicação.
 */
const corrigirSobreposicoes = (coords: Coordenada[]): CorrecaoSobreposicao => {
    const kinksAntes = contarKinks(coords)
    const areaAntes = areaEmHectares(coords)
    const duplicadosAntes = contarDuplicadosDoAnel(coords)

    const semCorrecao: CorrecaoSobreposicao = {
        coords,
        resolvido: kinksAntes === 0,
        kinksAntes,
        kinksDepois: kinksAntes,
        duplicadosAntes,
        duplicadosRemovidos: 0,
        formatoAlterado: false,
        areaAntes,
        areaDepois: areaAntes,
        variacaoArea: 0,
        descartado: 0,
        arriscado: false,
    }

    // Nada a fazer: sem sobreposição e sem vértices repetidos
    if (kinksAntes === 0 && duplicadosAntes === 0) return semCorrecao
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return semCorrecao

    const montarResultado = (
        bruto: Coordenada[],
        descartado: number,
        duplicadosRemovidos: number,
        formatoAlterado: boolean
    ): CorrecaoSobreposicao => {
        const areaDepois = areaEmHectares(bruto)
        const variacaoArea = areaAntes > 0 ? (Math.abs(areaDepois - areaAntes) / areaAntes) * 100 : 0
        const kinksDepois = contarKinks(bruto)

        return {
            coords: bruto,
            resolvido: kinksDepois === 0,
            kinksAntes,
            kinksDepois,
            duplicadosAntes,
            duplicadosRemovidos,
            formatoAlterado,
            areaAntes,
            areaDepois,
            variacaoArea,
            descartado,
            arriscado: variacaoArea > LIMITE_VARIACAO_AREA || descartado > LIMITE_VARIACAO_AREA,
        }
    }

    // 1) Sempre limpa os vértices repetidos primeiro — passo mais barato e sem risco
    const semDuplicados = removerDuplicadosDoAnel(coords)
    let atual = semDuplicados.removidos > 0 ? semDuplicados.coords : coords
    const duplicadosRemovidos = semDuplicados.removidos

    // Resolveu só com isso: é o caso mais comum (trator parado repetindo coordenada).
    // O formato do contorno não muda, então não há o que revisar.
    if (contarKinks(atual) === 0) {
        if (duplicadosRemovidos === 0) return semCorrecao
        return montarResultado(atual, 0, duplicadosRemovidos, false)
    }

    /**
     * 2) Divide no ponto de cruzamento e mantém a maior parte.
     *
     * Em loop: um contorno com várias sobreposições pode não ser resolvido em uma
     * única passada, e parar no meio deixaria o alerta aceso mesmo "após a correção".
     * Só seguimos enquanto o número de kinks realmente diminui.
     */
    let descartadoAcumulado = 0
    let rodouUnkink = false

    for (let passo = 0; passo < MAX_PASSOS_CORRECAO; passo++) {
        const kinksAtuais = contarKinks(atual)
        if (kinksAtuais === 0) break

        try {
            const partes = turf.unkinkPolygon(paraPoligonoTurf(atual))
            if (!partes?.features?.length) break

            const areas = partes.features.map((feature) => turf.area(feature))
            const areaTotal = areas.reduce((soma, valor) => soma + valor, 0)
            const indiceMaior = areas.indexOf(Math.max(...areas))

            const maior = partes.features[indiceMaior]
            const resultado = maior.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }))
            if (contarVerticesReais(resultado) < MINIMO_VERTICES_POLIGONO) break

            // Sem progresso: aborta para não girar em falso nem degradar o contorno
            if (contarKinks(resultado) >= kinksAtuais) break

            if (areaTotal > 0) {
                descartadoAcumulado += ((areaTotal - areas[indiceMaior]) / areaTotal) * 100
            }
            atual = resultado
            rodouUnkink = true
        } catch {
            break
        }
    }

    // Nem os duplicados nem o unkink alteraram o contorno
    if (atual === coords) return semCorrecao

    return montarResultado(atual, descartadoAcumulado, duplicadosRemovidos, rodouUnkink)
}

/**
 * Valor de `correcaoSobreposicoes` enquanto o cálculo não é necessário.
 *
 * `corrigirSobreposicoes` é O(n²) e só interessa dentro do modo de edição; fora
 * dele devolvemos este objeto para não bloquear a thread. Os campos ficam
 * zerados, o que deixa `temAlgoParaCorrigir` falso e desabilita o botão de
 * correção — que aliás nem está montado fora da edição.
 *
 * Congelado e reutilizado: como é a identidade que alimenta os `useMemo` que
 * dependem de `correcaoSobreposicoes`, um objeto novo a cada render invalidaria
 * essas memoizações sem motivo.
 */
const CORRECAO_NAO_CALCULADA: CorrecaoSobreposicao = Object.freeze({
    coords: [],
    resolvido: true,
    kinksAntes: 0,
    kinksDepois: 0,
    duplicadosAntes: 0,
    duplicadosRemovidos: 0,
    formatoAlterado: false,
    areaAntes: 0,
    areaDepois: 0,
    variacaoArea: 0,
    descartado: 0,
    arriscado: false,
}) as CorrecaoSobreposicao

/**
 * Simplifica apenas trechos retos do contorno (Douglas-Peucker).
 *
 * Diferente da redução por distância, aqui o critério é o desvio perpendicular:
 * um vértice só é removido se a linha continuar praticamente no mesmo lugar sem
 * ele. Assim uma reta com 50 vértices colapsa para 2, enquanto cantos e curvas
 * são preservados.
 *
 * Vive no escopo do módulo porque a preparação da edição (`prepararEdicao`)
 * precisa chamá-la fora de qualquer hook.
 */
const reduzirRetas = (coords: Coordenada[], toleranciaMetros: number): Coordenada[] => {
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return coords

    const primeiro = coords[0]
    const fechado = anelFechado(coords)

    // turf.simplify espera um anel fechado
    const anel = fechado ? coords : [...coords, { ...primeiro }]
    if (anel.length < 4) return coords

    // A tolerância do turf.simplify é em graus — convertemos a partir dos metros
    const grausPorMetro = 1 / 111320
    const tolerancia = toleranciaMetros * grausPorMetro

    try {
        /**
         * Simplificamos como LineString, e não como Polygon: turf.simplify lança
         * "invalid polygon" quando o anel cairia abaixo de 4 coordenadas, e não
         * simplifica anéis auto-interseccionados — justamente o caso dos
         * contornos que este módulo precisa corrigir.
         */
        const linha = turf.lineString(anel.map(({ lat, lng }) => [lng, lat]))
        const simplificada = turf.simplify(linha, { tolerance: tolerancia, highQuality: true })

        const resultado: Coordenada[] = simplificada.geometry.coordinates.map(([lng, lat]) => ({ lat, lng }))

        // Se a simplificação degenerar o polígono, mantém o original
        if (contarVerticesReais(resultado) < MINIMO_VERTICES_POLIGONO) return coords

        // Garante que o anel continue fechado após a simplificação
        if (!anelFechado(resultado)) resultado.push({ ...resultado[0] })

        return fechado ? resultado : resultado.slice(0, -1)
    } catch {
        return coords
    }
}

/**
 * Busca a menor tolerância que traz o contorno abaixo de `LIMITE_VERTICES_EDICAO`.
 *
 * Preferimos a tolerância mais conservadora que resolve, em vez de aplicar a
 * mais agressiva de uma vez: o contorno é dado que vira área faturada, então
 * cada metro de tolerância a mais é formato perdido sem necessidade. Quando
 * nenhuma tolerância da escala basta, devolvemos a melhor tentativa com
 * `dentroDoLimite: false` — a decisão de editar assim mesmo fica com o usuário.
 */
const prepararEdicao = (coords: Coordenada[]): PreparacaoEdicao => {
    const total = contarVerticesReais(coords)
    const areaAntes = areaEmHectares(coords)

    const semReducao: PreparacaoEdicao = {
        total,
        coords,
        aposReducao: total,
        tolerancia: 0,
        dentroDoLimite: total <= LIMITE_VERTICES_EDICAO,
        variacaoArea: 0,
    }

    if (total <= LIMITE_VERTICES_EDICAO) return semReducao

    let melhor = semReducao

    for (const tolerancia of TOLERANCIAS_SIMPLIFICACAO_AUTO) {
        const reduzido = reduzirRetas(coords, tolerancia)
        const aposReducao = contarVerticesReais(reduzido)

        // Tolerância sem efeito (ou que degenerou o polígono): tenta a próxima
        if (aposReducao >= melhor.aposReducao) continue

        const areaDepois = areaEmHectares(reduzido)
        const variacaoArea = areaAntes > 0 ? (Math.abs(areaDepois - areaAntes) / areaAntes) * 100 : 0

        melhor = {
            total,
            coords: reduzido,
            aposReducao,
            tolerancia,
            dentroDoLimite: aposReducao <= LIMITE_VERTICES_EDICAO,
            variacaoArea,
        }

        // Primeira tolerância que resolve — não escala mais que o necessário
        if (melhor.dentroDoLimite) break
    }

    return melhor
}

interface Props {
    isOpen: boolean
    onClose: () => void
    talhaoKml: any[]
    setTalhaoKml: React.Dispatch<React.SetStateAction<any[]>>
    setTalhaoArea?: React.Dispatch<React.SetStateAction<string>>
    setMalha: React.Dispatch<React.SetStateAction<any[]>>
    setMalhaShp: React.Dispatch<React.SetStateAction<any[]>>
    setPontosTotalMalha: React.Dispatch<React.SetStateAction<number>>
}

/**
 * Portão de montagem: `correcaoSobreposicoes` (e as prévias de redução) chamam
 * `turf.kinks`/`unkinkPolygon`, que são O(n²). Com o modal fechado o
 * `polygonRef` é nulo, então `lerCoordenadasDoPath()` devolve `[]` e o cálculo
 * cai no fallback `area` — que o efeito de `talhaoKml` preenche. Resultado: cada
 * troca de talhão pagava ~700ms de geometria por um modal que nem está na tela.
 * O `destroyOnClose` do antd só destrói os filhos, não este componente.
 */
const ModalCriarMalha: React.FC<Props> = (props) => {
    if (!props.isOpen) return null
    return <ConteudoCriarMalha {...props} />
}

const ConteudoCriarMalha: React.FC<Props> = ({ isOpen, onClose, talhaoKml, setTalhaoKml, setTalhaoArea, setMalha, setMalhaShp, setPontosTotalMalha }) => {
    const mapRef = useRef<google.maps.Map | null>(null)
    const polygonRef = useRef<google.maps.Polygon | null>(null)

    /**
     * Marca que o próximo `area` veio da simplificação feita para viabilizar a
     * edição, e não de um KML novo.
     *
     * O efeito que observa `area` trata toda mudança como carga de contorno: ao
     * não encontrar sobreposições ele desliga `editandoKml` e zera o grid. Como a
     * preparação altera `area` justamente para ENTRAR na edição — e pode ter
     * resolvido os kinks no caminho —, sem esta flag o modo era derrubado 100ms
     * depois de ligar.
     */
    const areaVeioDaPreparacao = useRef<boolean>(false)

    const [loadingKmlAdjustment, setLoadingKmlAdjustment] = useState(false)


    const [kmlComCruzamento, setKmlComCruzamento] = useState(null)
    const [disable, setDisable] = useState(true)
    const [validoFile, setValidoFile] = useState(false)
    const [area, setArea] = useState([])
    const [editandoKml, setEditandoKml] = useState<boolean>(false)
    const [pontosKinks, setPontosKinks] = useState<{ lat: number; lng: number }[]>([])
    const [openReducaoTrilhas, setOpenReducaoTrilhas] = useState<boolean>(false)
    const [distanciaTrilhas, setDistanciaTrilhas] = useState<number>(1)
    const [openReducaoRetas, setOpenReducaoRetas] = useState<boolean>(false)
    const [toleranciaRetas, setToleranciaRetas] = useState<number>(1)
    const [openCorrigirKinks, setOpenCorrigirKinks] = useState<boolean>(false)
    /**
     * Popover que intercepta a entrada na edição quando o contorno tem vértices
     * demais. Fica aberto até o usuário simplificar ou optar por editar assim
     * mesmo — `editandoKml` só liga depois dessa escolha.
     */
    const [openPrepararEdicao, setOpenPrepararEdicao] = useState<boolean>(false)
    /**
     * Contador incrementado a cada edição do path do polígono. O path é mutável e
     * vive fora do React, então precisamos de um gatilho explícito para recalcular
     * as prévias depois de arrastar ou remover um vértice.
     */
    const [contornoVersao, setContornoVersao] = useState<number>(0)

    //GRID
    const [rotation, setRotation] = useState<number>(0)
    const [movendo, setMovendo] = useState<boolean>(false)
    const [areaHa, setAreaHa] = useState<number>(0)
    const [gridLevel, setGridLevel] = useState<string>('')
    const [position, setPosition] = useState<{ x: 0; y: 0 }>({ x: 0, y: 0 })
    const [grid, setGrid] = useState<GridMalha[]>([])
    const [gridOriginal, setGridOriginal] = useState<GridMalha[]>([])
    const [gridOriginalRotacao, setGridOriginalRotacao] = useState<GridMalha[]>([])
    const [malhaPolygon, setMalhaPolygon] = useState<GridPolygon[]>([])
    const [malhaPolygonOriginal, setMalhaPolygonOriginal] = useState<GridPolygon[]>([])
    const [tamanhoGridOriginal, setTamanhoGridOriginal] = useState<number>(0)
    const [abrirModal, setAbrirModal] = useState<boolean>(false)
    const [pontosSelecionados, setPontosSelecionados] = useState([])
    const [nomeTalhao, setNomeTalhao] = useState<string>('')
    const [clicou, setClicou] = useState<boolean>(false)
    const [mousePosition, setMousePosition] = useState<[number, number] | null>(null)
    const [mapClickEvent, setMapClickEvent] = useState<{ lat: number; lng: number; ts: number } | null>(null)

    //CONFIGURAÇÕES
    const [tab_container_atual, setTab_container_atual] = useState<string>('Mapa')
    const [novoPonto, setNovoPonto] = useState<boolean>(false)
    const [moverPonto, setMoverPonto] = useState<boolean>(false)
    const [removerPonto, setRemoverPonto] = useState<boolean>(false)
    const [selecionarPonto, setSelecionarPonto] = useState<boolean>(false)
    const [abaAtual, setAbaAtual] = useState<number>(0)
    const abasNome = ['Mapa', 'Pontos', 'Ordenacao']
    const [confirmado, setConfirmado] = useState<boolean>(false)
    const [arquivoSelecionado, setArquivoSelecionado] = useState<string>('')
    const [rotacaoInput, setRotacaoInput] = useState<string>('')
    const [reset, setReset] = useState(true)
    const [quadrante, setQuadrante] = useState<number>(0)

    const padrao = useMemo(() => [
        [-15.7801, -47.9292],
        [-15.7801, -47.9192],
        [-15.7701, -47.9192],
        [-15.7701, -47.9292],
        [-15.7801, -47.9292],
    ], [])

    const kmlCoordsLatLng = useMemo(() => {
        if (area.length <= 0) return padrao
        return area.map((item) => [item.lat, item.lng])
    }, [area, padrao])

    // ancora retorna [lng, lat] do turf — convertemos para {lat, lng}
    const ancoraRaw = useMemo(() => calcularCentroTurf(kmlCoordsLatLng), [kmlCoordsLatLng])
    const ancora = useMemo(() => {
        if (!ancoraRaw) return null
        return { lat: ancoraRaw[1], lng: ancoraRaw[0] }
    }, [ancoraRaw])

    const polygonKml = useMemo(() => criarPoligono(kmlCoordsLatLng), [kmlCoordsLatLng])

    // Se talhaoKml já tiver coordenadas, usa direto (sem precisar de upload KML)
    useEffect(() => {
        if (talhaoKml?.length > 0) {
            // Aceita tanto [{lat, lng}] quanto [[lat, lng]]
            const areaCoord = talhaoKml.map((item) => {
                if (Array.isArray(item)) {
                    return { lat: Number(item[0]), lng: Number(item[1]) }
                }
                return { lat: Number(item.lat), lng: Number(item.lng) }
            })

            // Valida se todas as coordenadas são números válidos
            const valido = areaCoord.every((c) => !isNaN(c.lat) && !isNaN(c.lng))
            if (!valido) return

            setArea(areaCoord)
            const calcArea = calculoAreaCoord([areaCoord])
            setAreaHa(calcArea)
            if (calcArea > 0) {
                setValidoFile(true)
                setDisable(false)
            }
        }
    }, [talhaoKml])

    const aoCarregarArquivo = useCallback((rawData: string, base64Data) => {
        extrairCoordenadas(rawData)
    }, [])

    const converterXmlDeString = async (xmlString: string): Promise<Document> => {
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(xmlString, 'application/xml')
        return xmlDoc
    }

    const extrairCoordenadas = async (kmlString: string) => {
        try {
            const domKml = await converterXmlDeString(kmlString)

            const dataGeoJson = kml(domKml)
            setNomeTalhao(dataGeoJson?.features[0]?.properties?.name)
            const talhoes = []

            const dados = dataGeoJson.features
            const filtrado = dados.filter((data) => data.geometry.type === 'Polygon')

            if (filtrado.length > 0) {
                for (let i = 0; i < filtrado.length; i += 1) {
                    const fragment = filtrado[i]
                    const talhaoPoly = []

                    const coordinates = fragment.geometry.coordinates[0]

                    for (let ii = 0; ii < coordinates.length; ii += 1) {
                        const coord = coordinates[ii]

                        talhaoPoly.push({ lat: coord[1], lng: coord[0] })
                    }

                    talhoes.push(talhaoPoly)
                }

                const areaCoord = talhoes[0].map((item) => {
                    return { lat: item.lat, lng: item.lng }
                })
                setArea(areaCoord)
                setTalhaoKml(areaCoord)
                const calcArea = calculoAreaCoord([areaCoord])
                setAreaHa(calcArea)
                setTalhaoArea(String(calcArea))
                if (calcArea <= 0) {
                    errorAlert('Arquivo Kml inválido!')
                    setValidoFile(false)
                    setTimeout(() => {
                        setDisable(true)
                    }, 1000)
                } else {
                    setValidoFile(true)
                    setTimeout(() => {
                        setDisable(false)
                    }, 1000)
                }
            } else {
                errorAlert('Arquivo kml inválido!')
                setValidoFile(false)
            }
        } catch (error) {
            console.error('Erro ao processar XML:', error)
        }
    }

    const verificarAvancar = useCallback(() => {
        const temValor = grid.some((item) => 'posicao' in item)
        const tamanhoGrid = grid.filter(({ center }) =>
            turf.booleanPointInPolygon(turf.point([center[1], center[0]]), polygonKml)
        )
        if (temValor && abaAtual === 2) {
            return setAbrirModal(true)
        }

        if (tamanhoGridOriginal !== tamanhoGrid.length && abaAtual === 1) {
            return setAbrirModal(true)
        }

        voltar()
    }, [grid, polygonKml, abaAtual, tamanhoGridOriginal])

    const avancar = useCallback(() => {
        if (abaAtual < abasNome.length - 1) {
            setAbaAtual((prev) => prev + 1)
        }
    }, [abaAtual, abasNome.length])

    const voltar = useCallback(() => {
        if (abaAtual > 0) {
            setAbaAtual((prev) => prev - 1)
        }
    }, [abaAtual])

    const resetarGrid = useCallback(() => {
        setReset(false)
        setGrid(gridOriginalRotacao)
        setPontosSelecionados([])
        setMousePosition(null)
    }, [gridOriginalRotacao])

    // Função para BAIXAR o arquivo (KML ou SHP) sem fechar o modal
    const baixarMalha = useCallback((tipo: string) => {
        if (tipo === 'Kml') {
            baixarGridKML(grid, nomeTalhao)
        } else {
            gerarArquivoShp(grid, nomeTalhao)
        }
    }, [grid, nomeTalhao])

    // Função para SALVAR a malha (envia dados para o pai e fecha o modal)
    const salvarMalha = useCallback(() => {
        const gridValido = grid.filter(g => !g.oculto)

        const pontosFormatados = gridValido.map((item, index) => ({
            INDICE: item.posicao ?? (index + 1),
            NOME: item.posicao ? String(item.posicao) : String(index + 1),
            LATITUDE: item.center[0],
            LONGITUDE: item.center[1],
            COR: null
        }))

        setMalha(pontosFormatados)

        const features = gridValido.map((item, index) => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [String(item.center[1]), String(item.center[0])]
            },
            properties: {
                INDICE: String(item.posicao ?? (index + 1)),
                NOME: item.posicao ? String(item.posicao) : String(index + 1),
                LATITUDE: String(item.center[0]),
                LONGITUDE: String(item.center[1]),
                COMENTARIO: null,
                ICONE: null,
                ALTITUDE: null,
                DATA: null,
                ANGULO: null,
                DISPLAY: null,
                ZONA: null,
            }
        }))

        // @ts-ignore
        const malhaGeoJson: any = {
            type: 'FeatureCollection',
            features: features,
            fileName: nomeTalhao || 'Malha'
        }

        setMalhaShp(malhaGeoJson)
        setPontosTotalMalha(pontosFormatados.length)

        // Fechar modal e limpar estado
        setArquivoSelecionado('')
        setAbaAtual(0)
        setTab_container_atual('Mapa')
        setGrid([])
        setPontosSelecionados([])
        onClose()
    }, [grid, nomeTalhao, setMalha, setMalhaShp, onClose])

    useEffect(() => {
        if (confirmado) {
            if (abaAtual === 2) {
                voltar()
                setAbrirModal(false)
                setSelecionarPonto(false)
                resetarGrid()
                setArquivoSelecionado('')
                setTimeout(() => {
                    setConfirmado(false)
                }, 500)
            }

            if (abaAtual === 1) {
                voltar()
                setAbrirModal(false)
                setGrid(gridOriginal)
                setRotation(0)
                setRotacaoInput('0')
                setNovoPonto(false)
                setMoverPonto(false)
                setRemoverPonto(false)
                setTimeout(() => {
                    setConfirmado(false)
                }, 500)
            }
        }
    }, [confirmado])

    /**
     * Ao entrar/sair do modo de edição o polígono é remontado (muda `editable`),
     * então o path só existe no tick seguinte. Este bump garante que as prévias
     * recalculem já com o path disponível, em vez de esperar um re-render alheio.
     */
    useEffect(() => {
        setContornoVersao((prev) => prev + 1)
    }, [editandoKml, area])

    useEffect(() => {
        if (area.length > 0 && isOpen) {
            /**
             * A simplificação da preparação também altera `area`, mas para entrar
             * na edição — não é carga de um contorno novo. Consome a flag e apenas
             * reavalia os marcadores, sem derrubar a edição nem zerar o grid.
             */
            const daPreparacao = areaVeioDaPreparacao.current
            areaVeioDaPreparacao.current = false

            // Pequeno timeout para garantir que o "loading" apareça se foi setado
            const timer = setTimeout(() => {
                const kinks = turf.kinks(polygonKml)

                if (kinks.features.length > 0) {
                    const pontos = kinks.features.map((f) => ({
                        lat: f.geometry.coordinates[1],
                        lng: f.geometry.coordinates[0],
                    }))
                    // Vindo da preparação o usuário já está entrando na edição —
                    // o alerta pedindo para habilitá-la só confundiria
                    if (!daPreparacao) {
                        warningAlert(`O contorno possui ${pontos.length} vértice(s) sobreposto(s), habilite o editar contorno para ajustar`)
                    }
                    setPontosKinks(pontos)
                    setKmlComCruzamento(true)

                    // NÃO seta setEditandoKml(false).
                    // Se o usuário está editando, continua. Se não está, continua.

                    setLoadingKmlAdjustment(false)
                    // warningAlert(`Contorno com ${pontos.length} cruzamento(s)!`)
                    return
                }

                setPontosKinks([])
                setKmlComCruzamento(false)
                setLoadingKmlAdjustment(false)

                // Contorno limpo por efeito da preparação: mantém o usuário na
                // edição que ele acabou de pedir e preserva o grid já gerado
                if (daPreparacao) return

                setEditandoKml(false)
                setGrid([])
                setGridOriginal([])
                setGridOriginalRotacao([])
                setTamanhoGridOriginal(null)
                setClicou(false)
                setRotation(0)
            }, 100)

            return () => clearTimeout(timer)
        }
    }, [area, isOpen, polygonKml])

    // Handler para salvar contorno editado — chamado manualmente pelo botão
    const salvarContorno = useCallback(() => {
        if (!polygonRef.current) return
        setLoadingKmlAdjustment(true)
        requestAnimationFrame(() => {
            const path = polygonRef.current?.getPath()
            if (!path) return
            const novaArea: { lat: number; lng: number }[] = []
            for (let i = 0; i < path.getLength(); i++) {
                const pt = path.getAt(i)
                novaArea.push({ lat: pt.lat(), lng: pt.lng() })
            }
            // Garante que o polígono esteja fechado
            if (novaArea.length > 0) {
                const first = novaArea[0]
                const last = novaArea[novaArea.length - 1]
                if (first.lat !== last.lat || first.lng !== last.lng) {
                    novaArea.push({ ...first })
                }
            }
            // Desativa modo edição antes de atualizar area para que o fitBounds centralize
            setEditandoKml(false)
            setArea(novaArea)
        })
    }, [])

    /**
     * Reavalia as sobreposições a partir do path atual do polígono no mapa.
     *
     * Diferente do módulo de grid, aqui o salvamento é manual: quem encerra a
     * edição é o botão "Salvar contorno". Esta função apenas mantém os
     * marcadores em dia — sair da edição sozinho tiraria o usuário do modo no
     * meio do trabalho (por exemplo depois de corrigir as sobreposições, mas
     * antes de rodar o redutor de trilhas).
     */
    const verificarKinksDoPoligono = useCallback(() => {
        if (!polygonRef.current) return

        const path = polygonRef.current.getPath()
        const coords: [number, number][] = []
        for (let i = 0; i < path.getLength(); i++) {
            const pt = path.getAt(i)
            coords.push([pt.lng(), pt.lat()])
        }

        // Fechar o anel se necessário
        if (coords.length >= 3) {
            const first = coords[0]
            const last = coords[coords.length - 1]
            if (first[0] !== last[0] || first[1] !== last[1]) {
                coords.push([...first])
            }
        }

        if (coords.length < 4) return

        const poly = turf.polygon([coords])
        const kinks = turf.kinks(poly)

        if (kinks.features.length > 0) {
            // Ainda tem cruzamentos — atualizar apenas os markers
            const pontos = kinks.features.map((f) => ({
                lat: f.geometry.coordinates[1],
                lng: f.geometry.coordinates[0],
            }))
            setPontosKinks(pontos)
            return
        }

        // Contorno limpo: some com os marcadores e libera o "Avançar", mas segue
        // em edição até o usuário salvar
        setPontosKinks([])
        setKmlComCruzamento(false)
    }, [])

    // Chave de comparação de coordenada — reexporta o helper de módulo
    const chaveDaCoordenada = useCallback(
        (lat: number, lng: number) => chaveDeCoordenada(lat, lng),
        []
    )

    // Lê o contorno atual direto do path do polígono no mapa
    const lerCoordenadasDoPath = useCallback(() => {
        if (!polygonRef.current) return []

        const path = polygonRef.current.getPath()
        const coords: Coordenada[] = []
        for (let i = 0; i < path.getLength(); i++) {
            const pt = path.getAt(i)
            coords.push({ lat: pt.lat(), lng: pt.lng() })
        }
        return coords
    }, [])

    /** Reescreve o path do polígono no mapa com o contorno informado */
    const escreverCoordenadasNoPath = useCallback((coords: Coordenada[]) => {
        if (!polygonRef.current) return
        const path = polygonRef.current.getPath()
        path.clear()
        coords.forEach(({ lat, lng }) => { path.push(new google.maps.LatLng(lat, lng)) })
    }, [])

    /**
     * Correção do contorno atual.
     *
     * Prefere o path do mapa, porque durante a edição ele divirge do estado `area`
     * e a prévia precisa refletir exatamente o que será aplicado. Cai para `area`
     * enquanto o path não existe — o `polygonRef` só é preenchido no `onLoad` do
     * polígono, depois de a API do Google Maps carregar.
     *
     * SÓ CALCULA DENTRO DA EDIÇÃO. `corrigirSobreposicoes` chama `turf.kinks` até
     * 6 vezes (contagem inicial, pós-duplicados, e a cada passo do unkink), e cada
     * chamada é O(n²) — ~1,2s num contorno de 14 mil vértices, ou seja ~10s no
     * total. Todos os consumidores deste valor (botão e popover de correção
     * automática) vivem dentro do bloco `editandoKml`, então fora dele pagávamos
     * segundos de bloqueio da thread por um resultado que ninguém lia — era isso
     * que travava a abertura do modal.
     *
     * ATENÇÃO às dependências: o path é um objeto mutável fora do React, então
     * nada aqui reage sozinho a ele. `contornoVersao` cobre as edições manuais e
     * `editandoKml` cobre a entrada no modo de edição (que é quando o botão
     * aparece) — sem ele o valor calculado com o path ainda vazio ficava em cache
     * e o botão só habilitava num re-render alheio, como dar zoom no mapa.
     */
    const correcaoSobreposicoes = useMemo(() => {
        void contornoVersao

        if (!editandoKml) return CORRECAO_NAO_CALCULADA

        const doPath = lerCoordenadasDoPath()
        return corrigirSobreposicoes(doPath.length > 0 ? doPath : area)
    }, [area, contornoVersao, editandoKml, lerCoordenadasDoPath])

    // Há sobreposição ou vértice repetido a tratar
    const temAlgoParaCorrigir =
        correcaoSobreposicoes.kinksAntes > 0 || correcaoSobreposicoes.duplicadosAntes > 0

    const aplicarCorrecaoSobreposicoes = useCallback(() => {
        if (!polygonRef.current) return

        const {
            coords,
            resolvido,
            arriscado,
            variacaoArea,
            duplicadosRemovidos,
            formatoAlterado,
            kinksAntes,
            kinksDepois,
        } = correcaoSobreposicoes

        if (!temAlgoParaCorrigir) {
            warningAlert('Nenhuma sobreposição ou vértice repetido encontrado no contorno.')
            return
        }

        // Correção possível, mas alteraria demais o contorno — decisão fica com o usuário
        if (arriscado) {
            errorAlert(
                `Correção não aplicada: alteraria ${variacaoArea.toFixed(1)}% da área. Ajuste os vértices manualmente.`
            )
            return
        }

        // Nada mudou: nem duplicados removidos, nem o unkink rodou
        if (duplicadosRemovidos === 0 && !formatoAlterado) {
            warningAlert('Não foi possível corrigir automaticamente. Ajuste os vértices manualmente.')
            return
        }

        escreverCoordenadasNoPath(coords)
        setOpenCorrigirKinks(false)
        setContornoVersao((prev) => prev + 1)
        verificarKinksDoPoligono()

        // Detalha o que cada etapa resolveu — duplicados são a causa mais comum
        const partes: string[] = []
        if (duplicadosRemovidos > 0) {
            partes.push(`${duplicadosRemovidos} vértice(s) repetido(s) removido(s)`)
        }
        // Só creditamos a correção de sobreposição quando a etapa 2 realmente rodou:
        // vértices repetidos por si já acusam kink, e removê-los resolve sem ajustar nada
        if (kinksAntes > 0 && formatoAlterado) {
            partes.push(
                resolvido
                    ? `${kinksAntes} sobreposição(ões) corrigida(s)`
                    : `${kinksAntes - kinksDepois} de ${kinksAntes} sobreposição(ões) corrigida(s)`
            )
        }

        const resumo = partes.join(' e ')

        if (!resolvido) {
            errorAlert(
                `${resumo}. Ainda restam ${kinksDepois} sobreposição(ões) — ajuste os vértices manualmente.`
            )
            return
        }

        /**
         * O aviso de revisar só faz sentido se o formato do contorno mudou, ou seja
         * se a etapa 2 rodou. Remover vértices repetidos não move nenhum vértice.
         */
        successAlert(
            formatoAlterado
                ? `${resumo}. Revise o contorno antes de salvar.`
                : `Correção automática aplicada: ${resumo}.`
        )
    }, [
        correcaoSobreposicoes,
        temAlgoParaCorrigir,
        escreverCoordenadasNoPath,
        verificarKinksDoPoligono,
    ])

    /**
     * Reduz a quantidade de vértices do contorno mantendo apenas os que estão a
     * pelo menos `distanciaMinima` metros do último vértice preservado.
     * Não altera o mapa — serve tanto para a prévia quanto para a aplicação.
     */
    const calcularReducaoDeTrilhas = useCallback(
        (coords: Coordenada[], distanciaMinima: number) => {
            if (coords.length <= MINIMO_VERTICES_POLIGONO) return coords

            // O fechamento do anel é tratado à parte para nunca ser descartado
            const primeiro = coords[0]
            const ultimo = coords[coords.length - 1]
            const fechado =
                chaveDaCoordenada(primeiro.lat, primeiro.lng) === chaveDaCoordenada(ultimo.lat, ultimo.lng)
            const lista = fechado ? coords.slice(0, -1) : coords

            if (lista.length <= MINIMO_VERTICES_POLIGONO) return coords

            const mantidos: Coordenada[] = [lista[0]]

            for (let i = 1; i < lista.length; i++) {
                const atual = lista[i]
                const referencia = mantidos[mantidos.length - 1]

                const distancia = turf.distance(
                    turf.point([referencia.lng, referencia.lat]),
                    turf.point([atual.lng, atual.lat]),
                    { units: 'meters' }
                )

                if (distancia >= distanciaMinima) {
                    mantidos.push(atual)
                }
            }

            // Garante que o polígono continue válido mesmo com um intervalo agressivo
            if (mantidos.length < MINIMO_VERTICES_POLIGONO) return coords

            // O último vértice mantido pode ter ficado colado no primeiro ao fechar o anel
            if (mantidos.length > MINIMO_VERTICES_POLIGONO) {
                const inicio = mantidos[0]
                const fim = mantidos[mantidos.length - 1]
                const distanciaFechamento = turf.distance(
                    turf.point([inicio.lng, inicio.lat]),
                    turf.point([fim.lng, fim.lat]),
                    { units: 'meters' }
                )
                if (distanciaFechamento < distanciaMinima) {
                    mantidos.pop()
                }
            }

            return fechado ? [...mantidos, { ...mantidos[0] }] : mantidos
        },
        [chaveDaCoordenada]
    )

    /**
     * Prévia da redução exibida no popover.
     *
     * Lê o path do polígono no mapa (e não o estado `area`) porque durante a
     * edição do contorno os dois divergem — assim os números mostrados são
     * exatamente os que serão aplicados.
     */
    const previaReducaoTrilhas = useMemo(() => {
        // `openReducaoTrilhas` e `area` entram como dependências apenas para
        // forçar o recálculo ao abrir o popover e ao recarregar o contorno
        void openReducaoTrilhas
        void area
        void contornoVersao

        const coords = lerCoordenadasDoPath()
        const total = contarVerticesReais(coords)

        if (total === 0) {
            return { total: 0, aposReducao: 0, removidos: 0, porcentagem: 0 }
        }

        const reduzido = calcularReducaoDeTrilhas(coords, distanciaTrilhas)
        const aposReducao = contarVerticesReais(reduzido)
        const removidos = total - aposReducao
        const porcentagem = total > 0 ? (removidos / total) * 100 : 0

        return { total, aposReducao, removidos, porcentagem }
    }, [
        area,
        contornoVersao,
        openReducaoTrilhas,
        distanciaTrilhas,
        lerCoordenadasDoPath,
        calcularReducaoDeTrilhas,
    ])

    // Aplica a redução de trilhas no contorno
    const aplicarReducaoTrilhas = useCallback(() => {
        if (!polygonRef.current) return

        const coords = lerCoordenadasDoPath()
        if (coords.length <= MINIMO_VERTICES_POLIGONO) {
            warningAlert('Não é possível reduzir: o polígono precisa ter no mínimo 3 vértices.')
            return
        }

        const reduzido = calcularReducaoDeTrilhas(coords, distanciaTrilhas)
        const removidos = coords.length - reduzido.length

        if (removidos <= 0) {
            warningAlert(
                `Nenhum vértice foi removido com o intervalo de ${distanciaTrilhas}m. Tente um valor maior.`
            )
            return
        }

        escreverCoordenadasNoPath(reduzido)

        setOpenReducaoTrilhas(false)
        setContornoVersao((prev) => prev + 1)
        verificarKinksDoPoligono()

        successAlert(`${removidos} vértice(s) removido(s) pela redução de trilhas.`)
    }, [
        lerCoordenadasDoPath,
        escreverCoordenadasNoPath,
        calcularReducaoDeTrilhas,
        distanciaTrilhas,
        verificarKinksDoPoligono,
    ])

    // Prévia da simplificação de retas — lê o path do mapa, como a outra prévia
    const previaReducaoRetas = useMemo(() => {
        void openReducaoRetas
        void area
        void contornoVersao

        const coords = lerCoordenadasDoPath()
        const total = contarVerticesReais(coords)

        if (total === 0) {
            return { total: 0, aposReducao: 0, removidos: 0, porcentagem: 0 }
        }

        const reduzido = reduzirRetas(coords, toleranciaRetas)
        const aposReducao = contarVerticesReais(reduzido)
        const removidos = total - aposReducao
        const porcentagem = total > 0 ? (removidos / total) * 100 : 0

        return { total, aposReducao, removidos, porcentagem }
    }, [
        area,
        contornoVersao,
        openReducaoRetas,
        toleranciaRetas,
        lerCoordenadasDoPath,
    ])

    /**
     * Geometria da prévia desenhada no mapa enquanto o popover está aberto:
     * o contorno resultante e os vértices que serão descartados.
     */
    const previewNoMapa = useMemo(() => {
        const vazio = { contorno: [] as Coordenada[], descartados: [] as Coordenada[] }

        if (!openReducaoRetas && !openReducaoTrilhas && !openCorrigirKinks) return vazio

        void area
        void contornoVersao

        // Cai para `area` enquanto o path do mapa ainda não existe
        const doPath = lerCoordenadasDoPath()
        const coords = doPath.length > 0 ? doPath : area
        if (coords.length <= MINIMO_VERTICES_POLIGONO) return vazio

        let reduzido: Coordenada[]
        if (openCorrigirKinks) reduzido = correcaoSobreposicoes.coords
        else if (openReducaoRetas) reduzido = reduzirRetas(coords, toleranciaRetas)
        else reduzido = calcularReducaoDeTrilhas(coords, distanciaTrilhas)

        // Nada a mostrar quando a redução/correção devolveu o contorno intacto
        if (reduzido === coords) return vazio
        if (openCorrigirKinks && !temAlgoParaCorrigir) return vazio

        // Vértices que não sobreviveram à redução
        const mantidos = new Set(reduzido.map(({ lat, lng }) => chaveDaCoordenada(lat, lng)))
        const descartados = coords.filter(
            ({ lat, lng }) => !mantidos.has(chaveDaCoordenada(lat, lng))
        )

        return { contorno: reduzido, descartados }
    }, [
        area,
        contornoVersao,
        openReducaoRetas,
        openReducaoTrilhas,
        openCorrigirKinks,
        toleranciaRetas,
        distanciaTrilhas,
        correcaoSobreposicoes,
        temAlgoParaCorrigir,
        lerCoordenadasDoPath,
        calcularReducaoDeTrilhas,
        chaveDaCoordenada,
    ])

    // Aplica a simplificação de trechos retos no contorno
    const aplicarReducaoRetas = useCallback(() => {
        if (!polygonRef.current) return

        const coords = lerCoordenadasDoPath()
        if (coords.length <= MINIMO_VERTICES_POLIGONO) {
            warningAlert('Não é possível simplificar: o polígono precisa ter no mínimo 3 vértices.')
            return
        }

        const reduzido = reduzirRetas(coords, toleranciaRetas)
        const removidos = coords.length - reduzido.length

        if (removidos <= 0) {
            warningAlert(
                `Nenhum trecho reto foi encontrado com a tolerância de ${toleranciaRetas}m. Tente um valor maior.`
            )
            return
        }

        escreverCoordenadasNoPath(reduzido)

        setOpenReducaoRetas(false)
        setContornoVersao((prev) => prev + 1)
        verificarKinksDoPoligono()

        successAlert(`${removidos} vértice(s) removido(s) de trechos retos.`)
    }, [
        lerCoordenadasDoPath,
        escreverCoordenadasNoPath,
        toleranciaRetas,
        verificarKinksDoPoligono,
    ])

    /**
     * Preparação da edição — quantos vértices o contorno tem e para quanto a
     * simplificação automática consegue trazê-lo.
     *
     * Só calcula com o popover aberto: `prepararEdicao` roda `turf.simplify` uma
     * vez por tolerância da escala, o que não deve ser pago no render comum.
     * Fora da edição lê `area`, porque o path do mapa é reescrito pelas
     * ferramentas e é `area` que reflete o contorno carregado.
     */
    const preparacaoEdicao = useMemo(() => {
        if (!openPrepararEdicao) return null

        void contornoVersao
        const doPath = lerCoordenadasDoPath()
        return prepararEdicao(doPath.length > 0 ? doPath : area)
    }, [openPrepararEdicao, area, contornoVersao, lerCoordenadasDoPath])

    /**
     * Contagem de vértices do contorno atual — decide se o switch é interceptado.
     *
     * Conta pelo `getLength()` do path em vez de `lerCoordenadasDoPath()`: este
     * memo reavalia a cada `contornoVersao`, ou seja, a cada arraste de vértice,
     * e materializar 14 mil objetos só para contá-los é alocação no caminho
     * quente da edição.
     */
    const totalVerticesContorno = useMemo(() => {
        void contornoVersao

        const path = polygonRef.current?.getPath()
        const tamanho = path?.getLength() ?? 0
        if (tamanho === 0) return contarVerticesReais(area)

        // Desconta o vértice que só fecha o anel, como `contarVerticesReais` faz
        if (tamanho < 2) return tamanho
        const primeiro = path.getAt(0)
        const ultimo = path.getAt(tamanho - 1)
        const fechado =
            chaveDeCoordenada(primeiro.lat(), primeiro.lng()) ===
            chaveDeCoordenada(ultimo.lat(), ultimo.lng())

        return fechado ? tamanho - 1 : tamanho
    }, [area, contornoVersao])

    const contornoPesadoParaEdicao = totalVerticesContorno > LIMITE_VERTICES_EDICAO

    /**
     * Intercepta o switch: com contorno pesado, abre o popover de preparação em
     * vez de ligar a edição direto. Desligar nunca é interceptado — o usuário
     * precisa poder sair do modo a qualquer momento.
     */
    const alternarEdicaoKml = useCallback(() => {
        if (editandoKml) {
            setEditandoKml(false)
            return
        }

        if (contornoPesadoParaEdicao) {
            setOpenPrepararEdicao(true)
            return
        }

        setEditandoKml(true)
    }, [editandoKml, contornoPesadoParaEdicao])

    /** Aplica a simplificação sugerida e entra na edição já com o contorno leve */
    const aplicarPreparacaoEdicao = useCallback(() => {
        if (!preparacaoEdicao) return

        const { coords, total, aposReducao, tolerancia } = preparacaoEdicao
        const removidos = total - aposReducao

        if (removidos <= 0) {
            warningAlert('Não foi possível simplificar este contorno automaticamente.')
            return
        }

        /**
         * Vai para `area`, e não para o path do mapa como as ferramentas usadas
         * DENTRO da edição fazem.
         *
         * A diferença importa: aqui a simplificação acontece antes de entrar no
         * modo, e ligar `editandoKml` remonta o polígono a partir de
         * `kmlPolygonPaths` — que deriva de `area`. Escrever só no path seria
         * descartado nesse remonte. O `contornoVersao` é bumpado pelo efeito que
         * observa `area`, então as prévias recalculam sozinhas.
         */
        areaVeioDaPreparacao.current = true
        setArea(coords)

        setOpenPrepararEdicao(false)
        setEditandoKml(true)

        successAlert(
            `${removidos} vértice(s) removido(s) de trechos retos (tolerância de ${tolerancia}m). Contorno com ${aposReducao} vértices.`
        )
    }, [preparacaoEdicao])

    /** Entra na edição sem simplificar — o usuário assume a lentidão */
    const editarSemSimplificar = useCallback(() => {
        setOpenPrepararEdicao(false)
        setEditandoKml(true)
    }, [])

    /**
     * Prévia da preparação da edição — só o contorno resultante, em verde,
     * sobreposto ao contorno atual que já está desenhado no mapa.
     *
     * Deliberadamente NÃO marca os vértices descartados como as outras prévias
     * fazem: aqui são milhares deles, e criar um `Marker` para cada um traria de
     * volta exatamente o gargalo que este fluxo existe para evitar. Desenhar
     * dois polígonos não editáveis é barato, mesmo com 14 mil vértices.
     */
    const previewPreparacaoEdicao = useMemo(() => {
        if (!openPrepararEdicao) return [] as Coordenada[]
        if (!preparacaoEdicao) return [] as Coordenada[]
        if (preparacaoEdicao.aposReducao >= preparacaoEdicao.total) return [] as Coordenada[]
        return preparacaoEdicao.coords
    }, [openPrepararEdicao, preparacaoEdicao])

    // Remover vértice ao clicar com o botão direito
    const removerVertice = useCallback((e: google.maps.PolyMouseEvent) => {
        if (!editandoKml) return
        if (e.vertex == null) return
        if (!polygonRef.current) return

        const path = polygonRef.current.getPath()
        // Mínimo de 3 vértices para manter um polígono válido
        if (path.getLength() <= 3) {
            warningAlert('Não é possível remover: o polígono precisa ter no mínimo 3 vértices.')
            return
        }

        path.removeAt(e.vertex)
        setContornoVersao((prev) => prev + 1)
        verificarKinksDoPoligono()
    }, [editandoKml, verificarKinksDoPoligono])

    // Ao mover um vértice manualmente (arrastar), re-verificar kinks
    const aoEditarPoligono = useCallback(() => {
        if (!editandoKml) return
        setContornoVersao((prev) => prev + 1)
        verificarKinksDoPoligono()
    }, [editandoKml, verificarKinksDoPoligono])

    // Sair do modo de edição fecha as ferramentas — os popovers não fazem sentido fora dele
    useEffect(() => {
        if (editandoKml) return
        setOpenCorrigirKinks(false)
        setOpenReducaoTrilhas(false)
        setOpenReducaoRetas(false)
    }, [editandoKml])

    useEffect(() => {
        abasNome.forEach((item, index) => {
            if (index === abaAtual) {
                setTab_container_atual(item)
            }
        })

        if (abaAtual === 1) {
            setSelecionarPonto(false)
            setPontosSelecionados([])
        }

        if (abaAtual === 2) {
            setSelecionarPonto(true)
            setNovoPonto(false)
            setMoverPonto(false)
            setRemoverPonto(false)
        }
    }, [abaAtual])

    // Ajustar bounds do mapa quando as coordenadas mudam
    useEffect(() => {
        if (!mapRef.current || area.length === 0) return
        if (editandoKml) return

        const bounds = new google.maps.LatLngBounds()
        area.forEach(({ lat, lng }) => {
            bounds.extend({ lat, lng })
        })
        mapRef.current.fitBounds(bounds)
    }, [area, editandoKml])

    const center = useMemo(() => {
        if (area.length <= 0) return { lat: -14.235, lng: -51.925 }
        if (ancora) return ancora
        return { lat: kmlCoordsLatLng[0][0], lng: kmlCoordsLatLng[0][1] }
    }, [kmlCoordsLatLng, ancora, area])

    // Paths do polígono KML para o Google Maps
    const kmlPolygonPaths = useMemo(() => {
        return kmlCoordsLatLng.map(([lat, lng]) => ({ lat, lng }))
    }, [kmlCoordsLatLng])

    // Handler de clique no mapa — repassa para MalhaGrid via state
    const aoClicarNoMapa = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return
            setMapClickEvent({ lat: e.latLng.lat(), lng: e.latLng.lng(), ts: Date.now() })
        },
        []
    )

    const aoMoverMouseNoMapa = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return
            if (pontosSelecionados.length === 1) {
                setMousePosition([e.latLng.lat(), e.latLng.lng()])
            }
        },
        [pontosSelecionados]
    )

    const aoCarregarMapa = useCallback((map: google.maps.Map) => {
        mapRef.current = map

        // Aplica fitBounds se já tiver área
        if (area.length > 0) {
            const bounds = new google.maps.LatLngBounds()
            area.forEach(({ lat, lng }) => {
                bounds.extend({ lat, lng })
            })
            map.fitBounds(bounds)
        }
    }, [area])

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            title='Criar Malha'
            footer={null}
            width={'90vw'}
            style={{ top: 24 }}
            bodyStyle={{ height: '80vh', overflow: 'hidden' }}
            destroyOnClose
        >
            <div style={{ display: 'flex', position: 'relative', overflow: 'hidden', height: '100%' }}>
                <Configuracoes
                    disable={disable}
                    setDisable={setDisable}
                    aoCarregarArquivo={aoCarregarArquivo}
                    validoFile={validoFile}
                    setValidoFile={setValidoFile}
                    rotation={rotation}
                    setRotation={setRotation}
                    rotacaoInput={rotacaoInput}
                    setRotacaoInput={setRotacaoInput}
                    movendo={movendo}
                    setMovendo={setMovendo}
                    novoPonto={novoPonto}
                    setNovoPonto={setNovoPonto}
                    moverPonto={moverPonto}
                    setMoverPonto={setMoverPonto}
                    removerPonto={removerPonto}
                    setRemoverPonto={setRemoverPonto}
                    selecionarPonto={selecionarPonto}
                    setSelecionarPonto={setSelecionarPonto}
                    gridLevel={gridLevel}
                    setGridLevel={setGridLevel}
                    position={position}
                    setPosition={setPosition}
                    tab_container_atual={tab_container_atual}
                    setTab_container_atual={setTab_container_atual}
                    arquivoSelecionado={arquivoSelecionado}
                    setArquivoSelecionado={setArquivoSelecionado}
                    setGrid={setGrid}
                    setGridOriginal={setGridOriginal}
                    setGridOriginalRotacao={setGridOriginalRotacao}
                    setMalhaPolygon={setMalhaPolygon}
                    setMalhaPolygonOriginal={setMalhaPolygonOriginal}
                    setTamanhoGridOriginal={setTamanhoGridOriginal}
                    setQuadrante={setQuadrante}
                    setClicou={setClicou}
                    areaHectares={areaHa}
                    polygon={polygonKml}
                    kmlComCruzamento={kmlComCruzamento}
                    editandoKml={editandoKml}
                    grid={grid}
                    abaAtual={abaAtual}
                    abasNome={abasNome}
                    pontosSelecionados={pontosSelecionados}
                    area={area}
                    areaTamanho={areaHa}
                    quadrante={quadrante}
                    avancar={avancar}
                    voltar={verificarAvancar}
                    resetarGrid={resetarGrid}
                    nomeTalhao={nomeTalhao}
                    salvarMalha={salvarMalha}
                    baixarMalha={baixarMalha}
                    salvarContorno={salvarContorno}
                    openCorrigirKinks={openCorrigirKinks}
                    setOpenCorrigirKinks={setOpenCorrigirKinks}
                    correcaoSobreposicoes={correcaoSobreposicoes}
                    temAlgoParaCorrigir={temAlgoParaCorrigir}
                    aplicarCorrecaoSobreposicoes={aplicarCorrecaoSobreposicoes}
                    openReducaoTrilhas={openReducaoTrilhas}
                    setOpenReducaoTrilhas={setOpenReducaoTrilhas}
                    distanciaTrilhas={distanciaTrilhas}
                    setDistanciaTrilhas={setDistanciaTrilhas}
                    previaReducaoTrilhas={previaReducaoTrilhas}
                    aplicarReducaoTrilhas={aplicarReducaoTrilhas}
                    openReducaoRetas={openReducaoRetas}
                    setOpenReducaoRetas={setOpenReducaoRetas}
                    toleranciaRetas={toleranciaRetas}
                    setToleranciaRetas={setToleranciaRetas}
                    previaReducaoRetas={previaReducaoRetas}
                    aplicarReducaoRetas={aplicarReducaoRetas}
                    alternarEdicaoKml={alternarEdicaoKml}
                    openPrepararEdicao={openPrepararEdicao}
                    setOpenPrepararEdicao={setOpenPrepararEdicao}
                    preparacaoEdicao={preparacaoEdicao}
                    limiteVerticesEdicao={LIMITE_VERTICES_EDICAO}
                    totalVerticesContorno={totalVerticesContorno}
                    aplicarPreparacaoEdicao={aplicarPreparacaoEdicao}
                    editarSemSimplificar={editarSemSimplificar}
                />

                {/* @ts-ignore */}
                <GoogleMap
                    onLoad={aoCarregarMapa}
                    onClick={aoClicarNoMapa}
                    onMouseMove={aoMoverMouseNoMapa}
                    mapContainerStyle={{ borderRadius: 8, height: '99%', width: '100%', position: 'relative', overflow: 'hidden' }}
                    center={center}
                    zoom={area.length > 0 ? 15 : 4}
                    options={{
                        mapTypeId: 'hybrid',
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        disableDefaultUI: false,
                    }}
                    onRightClick={() => { }}
                >
                    <MalhaGrid
                        polygon={polygonKml}
                        novoPonto={novoPonto}
                        moverPonto={moverPonto}
                        removerPonto={removerPonto}
                        selecionarPonto={selecionarPonto}
                        rotation={rotation}
                        isSliding={movendo}
                        gridLevel={Number(gridLevel.replace(',', '.'))}
                        moverHorizontal={position?.x}
                        moverVertical={position?.y}
                        tab_selecionado={tab_container_atual}
                        abaAtual={abaAtual}
                        grid={grid}
                        setGrid={setGrid}
                        gridOriginal={gridOriginal}
                        setGridOriginal={setGridOriginal}
                        gridOriginalRotacao={gridOriginalRotacao}
                        setGridOriginalRotacao={setGridOriginalRotacao}
                        setTamanhoGridOriginal={setTamanhoGridOriginal}
                        abrirModal={abrirModal}
                        setAbrirModal={setAbrirModal}
                        confirmado={confirmado}
                        setConfirmado={setConfirmado}
                        pontosSelecionados={pontosSelecionados}
                        setPontosSelecionados={setPontosSelecionados}
                        mousePosition={mousePosition}
                        setMousePosition={setMousePosition}
                        ancora={ancora}
                        mapClickEvent={mapClickEvent}
                    />

                    {/* Polígono do KML — editável quando em modo edição */}
                    <GooglePolygon
                        paths={kmlPolygonPaths}
                        editable={editandoKml}
                        draggable={false}
                        onLoad={(polygon) => { polygonRef.current = polygon }}
                        onUnmount={() => { polygonRef.current = null }}
                        onRightClick={removerVertice}
                        onMouseUp={aoEditarPoligono}
                        options={{
                            /**
                             * O vermelho sinaliza contorno com problema, não "modo de
                             * edição": como aqui o salvamento é manual, o usuário
                             * continua editando depois de corrigir e precisa ver que o
                             * contorno já está limpo. Por isso a cor segue
                             * `kmlComCruzamento`, e não `editandoKml`.
                             */
                            fillOpacity: editandoKml ? 0.05 : 0,
                            fillColor: editandoKml && kmlComCruzamento ? '#ff4444' : undefined,
                            strokeColor: kmlComCruzamento ? '#ff4444' : 'cyan',
                            /**
                             * Mais grosso que a prévia (3) enquanto o popover de preparação
                             * compara os dois contornos: com traço fino a linha verde cobria
                             * esta nos trechos coincidentes — que são a maioria — e não dava
                             * para ver como o contorno está hoje.
                             */
                            strokeWeight: openPrepararEdicao ? 5 : editandoKml ? 3 : 2,
                            clickable: editandoKml,
                        }}
                    />

                    {/*
                      * Prévia da preparação da edição — como ficaria o contorno
                      * simplificado, sobre o atual. Não editável e sem markers de
                      * vértice: é o que permite comparar contornos de 14 mil
                      * pontos sem travar.
                      */}
                    <Render condition={previewPreparacaoEdicao.length > 0}>
                        <GooglePolygon
                            paths={previewPreparacaoEdicao}
                            editable={false}
                            draggable={false}
                            options={{
                                fillOpacity: 0,
                                strokeColor: '#00e676',
                                strokeWeight: 3,
                                clickable: false,
                                zIndex: 5,
                            }}
                        />
                    </Render>

                    {/* Prévia da redução — contorno resultante em verde */}
                    <Render condition={previewNoMapa.contorno.length > 0}>
                        <GooglePolygon
                            paths={previewNoMapa.contorno}
                            editable={false}
                            draggable={false}
                            options={{
                                fillOpacity: 0,
                                strokeColor: '#00e676',
                                strokeWeight: 3,
                                clickable: false,
                                zIndex: 5,
                            }}
                        />
                    </Render>

                    {/* Prévia da redução — vértices que serão removidos */}
                    {previewNoMapa.descartados.map((pt, i) => (
                        <Marker
                            key={`descartado_${i}`}
                            position={pt}
                            icon={{
                                path: google.maps.SymbolPath.CIRCLE,
                                fillColor: '#ff9100',
                                fillOpacity: 0.9,
                                strokeColor: '#7a3f00',
                                strokeWeight: 1,
                                scale: 4,
                            }}
                            clickable={false}
                            zIndex={6}
                        />
                    ))}

                    {/*
                      * Marcadores nos pontos de sobreposição.
                      *
                      * Ficam visíveis também durante a edição: é justamente aí que o
                      * usuário precisa saber onde estão os cruzamentos. O pino é alto
                      * (aponta para baixo, com a ponta no vértice) para não se perder
                      * no meio dos vértices editáveis do contorno.
                      */}
                    {pontosKinks.map((pt, i) => (
                        <Marker
                            key={`kink_${i}`}
                            position={pt}
                            icon={{
                                path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
                                fillColor: '#ff0000',
                                fillOpacity: 1,
                                strokeColor: '#000',
                                strokeWeight: 1,
                                scale: 1,
                                labelOrigin: new window.google.maps.Point(0, -28)
                            }}
                            label={{ text: '!', color: 'white', fontWeight: 'semi-bold' }}
                            zIndex={7}
                        />
                    ))}
                </GoogleMap>
                {/* Overlay de Loading Ajustando Contorno */}
                <Render condition={loadingKmlAdjustment}>
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        zIndex: 1000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column'
                    }}>
                        <Carregando animation legenda='Ajustando contorno...' justifyContent='center' height={100} />
                    </div>
                </Render>
            </div>
        </Modal>
    )
}

export default ModalCriarMalha
